import {InputSlider} from '#/atoms/input-slider';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {ActionMenuContentSectionInfoRow} from '#/components/actionmenu/subcomponents';
import {TextColorMarketIndicator} from '#/components/indicators/market-indicator';
import {maxSlippageMaxPercentage, maxSlippageMinPercentage} from '#/data/max-slippage-constants';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {getEstimatedPricePerToken} from '#/utils/get-estimated-price-per-token';
import {ComponentPropsWithoutRef, FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';

// Helpers
type StableTokenAmountValue = {stableTokenAmount: number};
type LandTokenAmountValue = {landTokenAmount: number};
type LpTokenAmountValue = {lpTokenAmount: number};

// Global
const MarketPrice: FunctionComponent = () => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();
  const {land, landMarketInfo} = useContext(ActionMenuContext);

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.marketPrice.title', {
          landTokenSymbol: land.tokenSymbol,
        }),
        right: t('pages.actionMenu.components.overview.marketPrice.value', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(landMarketInfo.marketPrice),
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const TransactionFee: FunctionComponent<{transactionFee: number}> = ({transactionFee}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.transactionFee.title'),
        right: t('pages.actionMenu.components.overview.transactionFee.value', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(transactionFee),
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};

// Limit
const AveragePrice: FunctionComponent<{targetPrice: number}> = ({targetPrice}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.averagePrice.title'),
        right: Number.isNaN(targetPrice)
          ? t('pages.actionMenu.components.contentSection.infoRow.empty')
          : t('pages.actionMenu.components.overview.averagePrice.value', {
              currencySymbol: displayPref.symbol,
              amount: displayPref.displayAmount(targetPrice),
            }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const MarketPriceDifference: FunctionComponent<{
  targetPrice: number;
}> = ({targetPrice}) => {
  const {landMarketInfo} = useContext(ActionMenuContext);
  const {t} = useTranslation('translation');

  const ratio = (targetPrice - landMarketInfo.marketPrice) / landMarketInfo.marketPrice;

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.averagePriceRatio.title'),
        right: t('pages.actionMenu.components.overview.averagePriceRatio.value', {
          value: ratio,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};

// Purchase | Sell
const Total: FunctionComponent<{total: number}> = ({total}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.total.title'),
        right: t('pages.actionMenu.components.overview.total.value', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(total),
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const Receive: FunctionComponent<{receive: number}> = ({receive}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.receive.title'),
        right: t('pages.actionMenu.components.overview.receive.value', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(receive),
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const MaxSlippage: FunctionComponent<{maxSlippage: number}> = ({maxSlippage}) => {
  const {t} = useTranslation('translation');

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.maxSlippage.title'),
        right: t('pages.actionMenu.components.overview.maxSlippage.value', {
          value: maxSlippage,
          decimals: 0,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const MaxSlippageInput: FunctionComponent<
  Pick<ComponentPropsWithoutRef<'input'>, 'onChange'> & {
    maxSlippagePercentage: number;
    setMaxSlippagePercentage: React.Dispatch<React.SetStateAction<number>>;
  }
> = ({setMaxSlippagePercentage, maxSlippagePercentage}) => {
  const {t} = useTranslation('translation');
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMaxSlippagePercentage(Number.parseInt(e.target.value));
  };

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.maxSlippageInput.title', {
          value: maxSlippagePercentage,
          decimals: 0,
        }),
        right: (
          <div className='flex items-center gap-2'>
            <InputSlider
              min={maxSlippageMinPercentage}
              max={maxSlippageMaxPercentage}
              step={1}
              value={maxSlippagePercentage}
              onChange={onChange}
            />
            <span className='w-8 text-right'>
              {t('utils.percentageFixed', {
                value: maxSlippagePercentage,
                decimals: 0,
              })}
            </span>
          </div>
        ),
        tooltip: t('pages.actionMenu.components.overview.maxSlippageInput.tooltip', {
          context: Number.isNaN(maxSlippagePercentage) ? 'invalid' : 'valid',
          value: maxSlippagePercentage,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const MinReceivedNative: FunctionComponent<LandTokenAmountValue> = ({landTokenAmount}) => {
  const {t} = useTranslation('translation');
  const {landMarketInfo} = useContext(ActionMenuContext);

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.minReceivedNative.title'),
        right: t('pages.actionMenu.components.overview.minReceivedNative.value', {
          currencySymbol: landMarketInfo.symbol,
          amount: landTokenAmount,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};

const MinReceivedStable: FunctionComponent<StableTokenAmountValue> = ({stableTokenAmount}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.minReceivedStable.title'),
        right: t('pages.actionMenu.components.overview.minReceivedStable.value', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(stableTokenAmount),
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const EstimatedPricePerToken: FunctionComponent<LandTokenAmountValue & StableTokenAmountValue> = ({
  landTokenAmount,
  stableTokenAmount,
}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();
  const {land} = useContext(ActionMenuContext);

  const estimatedPricePerToken = getEstimatedPricePerToken(landTokenAmount, stableTokenAmount);

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.estimatedPricePerToken.title', {
          landTokenSymbol: land.tokenSymbol,
        }),
        right: t('pages.actionMenu.components.overview.estimatedPricePerToken.value', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(estimatedPricePerToken),
        }),
        tooltip: t('pages.actionMenu.components.overview.estimatedPricePerToken.tooltip'),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};

// Rent
const RentLand: FunctionComponent<LandTokenAmountValue> = ({landTokenAmount}) => {
  const {t} = useTranslation('translation');
  const {landMarketInfo} = useContext(ActionMenuContext);

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.rentLand.title'),
        right: t('pages.actionMenu.components.overview.rentLand.value', {
          currencySymbol: landMarketInfo.symbol,
          amount: landTokenAmount,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const RentStable: FunctionComponent<StableTokenAmountValue> = ({stableTokenAmount}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.rentStable.title'),
        right: t('pages.actionMenu.components.overview.rentStable.value', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(stableTokenAmount),
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const TotalLandStable: FunctionComponent<LandTokenAmountValue & StableTokenAmountValue> = ({
  landTokenAmount,
  stableTokenAmount,
}) => {
  const {land} = useContext(ActionMenuContext);
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.totalLandStable.title'),
        right: t('pages.actionMenu.components.overview.totalLandStable.value', {
          landCurrencySymbol: land.tokenSymbol,
          landAmount: landTokenAmount,
          stableCurrencySymbol: displayPref.symbol,
          stableAmount: displayPref.displayAmount(stableTokenAmount),
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const RentReceive: FunctionComponent<LpTokenAmountValue> = ({lpTokenAmount}) => {
  const {t} = useTranslation('translation');
  const {landMarketInfo} = useContext(ActionMenuContext);

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.rentReceive.title'),
        right: t('pages.actionMenu.components.overview.rentReceive.value', {
          currencySymbol: landMarketInfo.lpSymbol,
          amount: lpTokenAmount,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};

// Stop Rent
const Burn: FunctionComponent<LpTokenAmountValue> = ({lpTokenAmount}) => {
  const {t} = useTranslation('translation');
  const {landMarketInfo} = useContext(ActionMenuContext);

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.burn.title'),
        right: t('pages.actionMenu.components.overview.burn.value', {
          currencySymbol: landMarketInfo.lpSymbol,
          amount: lpTokenAmount,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const ReceiveLand: FunctionComponent<LandTokenAmountValue> = ({landTokenAmount}) => {
  const {t} = useTranslation('translation');
  const {landMarketInfo} = useContext(ActionMenuContext);

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.receiveLand.title'),
        right: t('pages.actionMenu.components.overview.receiveLand.value', {
          currencySymbol: landMarketInfo.symbol,
          amount: landTokenAmount,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const ReceiveStable: FunctionComponent<StableTokenAmountValue> = ({stableTokenAmount}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.receiveStable.title'),
        right: t('pages.actionMenu.components.overview.receiveStable.value', {
          currencySymbol: displayPref.symbol,
          amount: stableTokenAmount,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};

// Borrow
const Borrow: FunctionComponent<LandTokenAmountValue> = ({landTokenAmount}) => {
  const {t} = useTranslation('translation');
  const {landMarketInfo} = useContext(ActionMenuContext);

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.borrow.title'),
        right: t('pages.actionMenu.components.overview.borrow.value', {
          currencySymbol: landMarketInfo.symbol,
          amount: landTokenAmount,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const Deposit: FunctionComponent<StableTokenAmountValue> = ({stableTokenAmount}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.deposit.title'),
        right: t('pages.actionMenu.components.overview.deposit.value', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(stableTokenAmount),
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const CollateralRatio: FunctionComponent<{collateralRatio: number | undefined}> = ({collateralRatio}) => {
  const {t} = useTranslation('translation');
  const finalCollateralRatio = collateralRatio ?? 0;

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.collateralRatio.title'),
        right: (
          <TextColorMarketIndicator
            numberPositivity={
              finalCollateralRatio === 0 ? 'zero' : finalCollateralRatio >= 100 ? 'positive' : 'negative'
            }
          >
            {t('pages.actionMenu.components.overview.collateralRatio.value', {
              value: finalCollateralRatio,
            })}
          </TextColorMarketIndicator>
        ),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const GiveBack: FunctionComponent<LandTokenAmountValue> = ({landTokenAmount}) => {
  const {t} = useTranslation('translation');
  const {landMarketInfo} = useContext(ActionMenuContext);

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.giveBack.title'),
        right: t('pages.actionMenu.components.overview.giveBack.value', {
          currencySymbol: landMarketInfo.symbol,
          amount: landTokenAmount,
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
const DifferenceFromLastPosition: FunctionComponent<StableTokenAmountValue> = ({stableTokenAmount}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('pages.actionMenu.components.overview.difference.title'),
        right: t('pages.actionMenu.components.overview.difference.value', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(stableTokenAmount),
        }),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};

export const ActionMenuOverviewInfoRow = {
  // Global
  MarketPrice,
  TransactionFee,
  // Limit
  AveragePrice,
  MarketPriceDifference,
  // Purchase | Sell
  Total,
  Receive,
  MaxSlippage,
  MaxSlippageInput,
  MinReceivedNative,
  MinReceivedStable,
  EstimatedPricePerToken,
  // Rent
  RentLand,
  RentStable,
  TotalLandStable,
  RentReceive,
  // Stop Rent
  Burn,
  ReceiveLand,
  ReceiveStable,
  // Borrow
  Borrow,
  Deposit,
  CollateralRatio,
  GiveBack,
  DifferenceFromLastPosition,
};
